import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import DateAndName from "../../molecules/DateAndName";
import { Attachment, Ticket } from "../../../services/swagger";
import Authorized from "../../atoms/Authorized";
import { useEffect, useState } from "react";
import useRefresh from "../../../hooks/useRefresh";
import useConfirm from "../../../hooks/useConfirm";
import { OpenAiService, TicketService } from "../../../services/api";
import FormInput from "../../molecules/FormInput";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import AskAiDialog from "../../molecules/AskAiDialog";
import AttachmentList from "../../molecules/AttachmentList";
import { useTranslation } from "react-i18next";
import TextBody from "../../atoms/TextBody";
import OptionsButton from "../../molecules/OptionsButton";
import EditIcon from "@mui/icons-material/Edit";
import GTranslateOutlinedIcon from "@mui/icons-material/GTranslateOutlined";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import { useMutation } from "@tanstack/react-query";
import LoadingTextBody from "../../atoms/LoadingTextBody";

interface TicketDetailsProps {
  ticket?: Ticket;
}

export default function TicketDetails(props: TicketDetailsProps) {
  const { t } = useTranslation("Ticket");
  const [editMode, setEditMode] = useState(false);
  const [aiResponseMode, setAiResponseMode] = useState(false);
  const [aiResponseText, setAiResponseText] = useState("");
  const [aiTitleText, setAiTitleText] = useState("");
  const [title, setTitle] = useState<string>(props.ticket?.title || "");
  const [content, setContent] = useState<string>(props.ticket?.details || "");
  const [openAskAIDialog, setOpenAskAIDialog] = useState(false);
  const refresh = useRefresh();
  const confirm = useConfirm();
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  useEffect(() => {
    if (!props.ticket) return;
    if (props.ticket?.details) setContent(props.ticket.details);
    if (props.ticket?.title) setTitle(props.ticket.title);

    const newAttachments: Attachment[] = [];
    for (const attachment of props.ticket?.attachments || []) {
      if (attachment.name) {
        newAttachments.push(attachment);
      } else {
        const obj = attachment as unknown as string[];
        let name = "";
        for (let i = 0; i < Object.keys(obj).length; i++) {
          name += obj[i];
        }
        const type = "image";
        const path = `/tickets/${props.ticket.id}/${name}`;
        newAttachments.push({
          name,
          type,
          path,
        });
      }
    }
    setAttachments(newAttachments);
  }, [props.ticket]);

  const handleResponseClose = () => {
    setAiResponseMode(false);
  };

  const { mutate: handleTranslate, isPending: isTranslationPending } =
    useMutation({
      mutationFn: async () => {
        setAiResponseMode(true);
        const contentReq = OpenAiService.createTranslatePrompt({
          createTranslateBody: {
            message: content,
          },
        });

        const titleReq = OpenAiService.createTranslatePrompt({
          createTranslateBody: {
            message: props.ticket?.title || "",
          },
        });
        const res = await Promise.all([contentReq, titleReq]);
        return res;
      },
      onSuccess: (data) => {
        setAiResponseText(data[0].data.data || "");
        setAiTitleText(data[1].data.data || "");
      },
    });

  const { mutate: handleSummarize, isPending: isSummarizePending } =
    useMutation({
      mutationFn: async () => {
        setAiResponseMode(true);
        return await OpenAiService.createSummarizePrompt({
          createSummarizeBody: {
            message: content,
          },
        });
      },
      onSuccess: (data) => {
        setAiResponseText(data.data?.data || "");
      },
    });

  const handleAskAIOpen = () => {
    setOpenAskAIDialog(true);
  };

  const handleDialogClose = () => {
    setOpenAskAIDialog(false);
  };

  const handleClickSave = () => {
    confirm(handleUpdateDetails);
  };

  const handleUpdateDetails = async () => {
    if (!props.ticket?._id) return;
    const res = await TicketService.updateTicketDetails({
      id: props.ticket?._id,
      updateTicketDetailsBody: {
        title,
        content,
      },
    });
    setEditMode(false);
    setAiResponseMode(false);
    if (res?.data?.success) refresh();
  };

  return (
    <Box
      sx={{
        p: 2,
        pb: 1,
        bgcolor: "background.default",
        borderRadius: (theme) => (theme.shape.borderRadius as number) / 8,
      }}
    >
      <Stack spacing={1}>
        <DateAndName
          date={props.ticket?.date}
          name={props.ticket?.user?.name || props.ticket?.person}
        />
        {editMode ? (
          <FormInput
            field="title"
            value={title}
            onChange={(v) => setTitle(v as string)}
            label=""
            type="string"
          />
        ) : (
          !aiResponseMode && (
            <Typography fontSize="medium" fontWeight={600}>
              {props.ticket?.title}
            </Typography>
          )
        )}
        {isTranslationPending || isSummarizePending ? (
          <Skeleton width={300} />
        ) : (
          aiResponseMode && (
            <Typography fontSize="medium" fontWeight={600}>
              {aiTitleText}
            </Typography>
          )
        )}
        {editMode ? (
          <FormInput
            field="content"
            value={content}
            onChange={(v) => setContent(v as string)}
            multiline
            label=""
            type="string"
            minRows={3}
          />
        ) : (
          !aiResponseMode && <TextBody>{props.ticket?.details}</TextBody>
        )}

        {isTranslationPending || isSummarizePending ? (
          <LoadingTextBody />
        ) : (
          aiResponseMode && (
            <Stack direction={"row"} spacing={"auto"}>
              <Typography whiteSpace="pre-wrap">{aiResponseText}</Typography>
            </Stack>
          )
        )}

        {props.ticket?.id !== undefined && (
          <AttachmentList
            ticketId={props.ticket.id.toString()}
            attachments={attachments}
          />
        )}

        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <Authorized role="agent">
            <Stack spacing={1} direction={"row"}>
              {editMode ? (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    onClick={handleClickSave}
                  >
                    {t("Save")}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => setEditMode(false)}
                  >
                    {t("Cancel")}
                  </Button>
                </>
              ) : (
                <>
                  <AskAiDialog
                    open={openAskAIDialog}
                    onClose={handleDialogClose}
                    defaultContent={content}
                  />

                  {aiResponseMode && !isTranslationPending && (
                    <Button
                      onClick={handleResponseClose}
                      size="small"
                      startIcon={<GTranslateOutlinedIcon />}
                    >
                      {t("ShowOriginal")}
                    </Button>
                  )}

                  <OptionsButton
                    options={[
                      {
                        icon: <EditIcon color="primary" />,
                        label: t("Edit"),
                        onClick: () => setEditMode(true),
                      },
                      {
                        icon: <GTranslateOutlinedIcon color="primary" />,
                        label: t("Translate"),
                        onClick: handleTranslate,
                      },
                      {
                        icon: <TextSnippetIcon color="primary" />,
                        label: t("Summarize"),
                        onClick: handleSummarize,
                      },
                      {
                        icon: <AutoAwesomeOutlinedIcon color="primary" />,
                        label: t("AskAI"),
                        onClick: handleAskAIOpen,
                      },
                    ]}
                  />
                </>
              )}
            </Stack>
          </Authorized>
        </Box>
      </Stack>
    </Box>
  );
}
